import { useState } from 'react';
import { ArrowRight, ChevronDown, ChevronUp, Info, LogOut, MessageSquare } from 'react-feather';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { swalMessage } from '@components/common';
import ChangeModal from '@components/modal/changeModal';
import DetailsModal from '@components/modal/detailsModal';
import MessageSendModal from '@components/modal/messageSendModal';
import { getMapViewList, getSeatLogList, getSeminarItemList, userList } from '@lib/api';
import { blockTicket, forceExitTicket } from '@lib/api/v2/ticket';
import dayjs from '@lib/dayjs';
import useHandleAPICall from '@lib/hooks/useHandleAPICall';
import useHandleError from '@lib/hooks/useHandleError';
import { media } from '@styles/mediaQuery';
import { checkIfCurrentFloor, swalReturnWarning } from '@util/commonFunctions';
import Format from '@util/formatter';
import styled from 'styled-components';

const Btns = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: space-between;
  margin-top: 10px;
`;
export const ButtonS = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 24%;
  padding: 8px 12px;
  border-radius: 4px;
  border: none;
  background-color: ${(props) => props.bg};
  color: ${(props) => (props.color ? props.color : '#fff')};
  font-weight: 600;
  font-size: 15px;
  > svg {
    height: 17px;
  }
  ${media.mobile`
    padding: 6px 10px;
    font-size: 13px;
    > svg {
      height: 15px;
    }
  `}
`;

const HistorySection = styled.div`
  margin-top: 16px;
  border-top: 1px solid #e9e9e9;
  padding-top: 12px;
`;

const HistoryToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 12px;
  background-color: #f8fcff;
  border: 1px solid #e9e9e9;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  color: #0f2a4a;
  cursor: pointer;

  > svg {
    height: 16px;
  }
`;

const HistoryTable = styled.table`
  width: 100%;
  margin-top: 10px;
  font-size: 13px;
  border-collapse: collapse;

  th,
  td {
    padding: 6px 8px;
    border-bottom: 1px solid #eee;
    text-align: left;
  }

  th {
    background-color: #f5f7fa;
    font-weight: 600;
  }

  tbody tr:hover {
    background-color: #fafafa;
  }
`;

const HistoryEmpty = styled.div`
  margin-top: 10px;
  padding: 16px;
  text-align: center;
  color: #888;
  font-size: 13px;
`;

const typeLabel = (t) => {
  switch (t) {
    case 1:
      return '일회권';
    case 2:
      return '기간권';
    case 3:
      return '시간정액권';
    case 4:
      return '세미나실';
    case 5:
      return '사물함';
    default:
      return '-';
  }
};

export const ModalBackground = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  z-index: 400;
`;

const CustomModalStyle = `
  .choice-modal-backdrop {
    z-index: 400 !important;
  }
  .choice-modal {
    z-index: 450 !important;
  }
`;

const ChoiceModal = ({ isOpen, onClose, seatData, setLists, lists, floorNum }) => {
  const { createRequest } = useHandleAPICall();
  const { catchError } = useHandleError();
  const [changeSeatModal, setChangeSeatModal] = useState(false);
  const [detailUserModal, setDetailUserModal] = useState(false);
  const [isSendMsg, setIsSendMsg] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [detailTarget, setDetailTarget] = useState(null);

  const openDetailWith = (target) => {
    setDetailTarget(target);
    setDetailUserModal(true);
  };

  const handleHistoryPhoneClick = async (phoneNum) => {
    if (!phoneNum || phoneNum === '-') return;
    try {
      const res = await createRequest(userList, seatData.merchantId, phoneNum);
      if (res.data?.[0]) openDetailWith(res.data[0]);
    } catch (error) {
      catchError(error);
    }
  };
  const showChangeSeatModal = () => {
    setChangeSeatModal(!changeSeatModal);
  };

  const showDetailModal = () => {
    setDetailUserModal(!detailUserModal);
  };
  const toggleIsSendMsg = () => {
    setIsSendMsg(!isSendMsg);
  };

  const loadHistory = async () => {
    if (historyLoaded || historyLoading) return;
    setHistoryLoading(true);
    try {
      const start = dayjs().subtract(6, 'day').format('YYYY-MM-DD');
      const end = dayjs().format('YYYY-MM-DD');
      const { data } = await createRequest(getSeatLogList, seatData.merchantId, start, end);
      const all = Array.isArray(data?.result) ? data.result : [];
      const seatId = seatData.id;
      const rows = all
        .filter((item) => item.seatId === seatId)
        .map((item) => ({
          dateTime: dayjs(item.createdAt).format('YYYY-MM-DD HH:mm:ss'),
          message: item.message || (item.subtype?.includes('exit') ? '퇴장' : ''),
          phoneNum: item.phoneNum || '-',
          ticketLabel: typeLabel(item.ticketType),
          ticketId: item.ticketId,
        }));
      setHistoryRows(rows);
      setHistoryLoaded(true);
    } catch (error) {
      catchError(error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    const next = !historyOpen;
    setHistoryOpen(next);
    if (next) loadHistory();
  };

  const outTicket = () => {
    const nowTime = dayjs();
    const diffTime = dayjs.duration(nowTime.diff(seatData.endDate)).asMilliseconds();
    if (diffTime > 0) {
      return swalReturnWarning(
        '이미 만료되어 퇴장처리가 불가능한 이용자입니다.\n초과결제 취소가 필요한 경우 퇴장여부의 초과결제 취소를 눌러 주세요.',
      );
    }
    const isSeminar = seatData.type === 4;
    const message = isSeminar
      ? '퇴장처리를 하시겠습니까?\n세미나실의 경우, 예약취소되어 현황에서도 사라집니다.'
      : '퇴장처리를 하시겠습니까?';
    swalMessage('warning', message, '퇴장', true, handleForceExit, undefined, '취소');
  };

  const handleForceExit = async () => {
    try {
      const ticketId = seatData.type === 4 ? seatData.id : seatData.ticketId;
      if (seatData.type === 4)
        await createRequest(blockTicket, { ticketId, block: 1 }); // 세미나실: 퇴장 + 차단 (예약 취소)
      else await createRequest(forceExitTicket, { ticketId }); // 일반 좌석: 퇴장만

      swalMessage('success', '퇴장처리가 완료되었습니다.');
      getSeatListApi();
      onClose();
    } catch (error) {
      catchError(error, '퇴장처리 중 에러가 발생했습니다. 다시 시도해 주세요.');
    }
  };

  const getSeatListApi = async () => {
    try {
      const res = await createRequest(getMapViewList, seatData.merchantId);

      const dateRange = Format.formatDateRange(new Date(), new Date());
      const seminarRes = await createRequest(
        getSeminarItemList,
        seatData.merchantId,
        dateRange.start,
        dateRange.end,
      );
      const usingSeminars = seminarRes.data.filter((seminar) => seminar.isUsing);
      /**
       * 세미나실 입장 현황이 표시되지 않는 현상 임시 수정
       * Note: 세미나실 이용 정보를 추가 fetch 후 기존 좌석 목록에 합치는 방식을 사용하였습니다.
       * 차후 좌석 목록 조회 API가 수정되면 Array.prototype.map() 부분은 삭제되어야 합니다.
       */
      const seatList = res.data
        .filter(
          (item) =>
            (item.type === 'seat' || item.type === 'seminar') &&
            checkIfCurrentFloor(item, floorNum),
        )
        .map((seat) => {
          try {
            if (seat.type !== 'seminar') return seat;
            const filtered = usingSeminars.filter((seminar) => seminar.seminarId === seat.id);
            if (filtered.length > 0)
              return {
                ...seat,
                ...filtered[0],
                id: seat.id,
                ticketId: filtered[0].id,
                status: 'using',
                type: 'seminar',
              };
            return seat;
          } catch (error) {
            return seat;
          }
        });
      setLists(seatList);
      // if (floorNum) {
      //   setLists(
      //     res.data
      //       .filter((item) => item.type === 'seat' || item.type === 'seminar')
      //       .filter((list) => list.floor === +floorNum),
      //   );
      // } else {
      //   setLists(res.data.filter((item) => item.type === 'seat' || item.type === 'seminar'));
      // }
    } catch (error) {
      catchError(error);
    }
  };
  return (
    <>
      <style>{CustomModalStyle}</style>
      {detailUserModal && (
        <DetailsModal
          isOpen={detailUserModal}
          close={() => {
            setDetailUserModal(false);
            setDetailTarget(null);
          }}
          choiceModalClose={onClose}
          seat={detailTarget || seatData}
          zIndex={detailTarget ? 1500 : 450}
        />
      )}
      {changeSeatModal && (
        <ChangeModal
          isOpen={changeSeatModal}
          close={showChangeSeatModal}
          choiceModalClose={onClose}
          seat={seatData}
          setLists={setLists}
          lists={lists}
        />
      )}
      {isSendMsg && (
        <MessageSendModal
          isOpen={isSendMsg}
          onClose={toggleIsSendMsg}
          phoneNum={seatData.phoneNum}
          mid={seatData.merchantId}
        />
      )}
      <Modal
        centered
        isOpen={isOpen}
        toggle={onClose}
        zIndex={450}
        modalClassName="choice-modal"
        backdropClassName="choice-modal-backdrop">
        <ModalHeader className="modal-title" toggle={onClose}>
          {seatData.type !== 4 ? (
            <>
              {seatData.name ? `[${seatData.name}] ` : ''}
              {seatData.number}번 좌석
            </>
          ) : (
            '세미나실 이용'
          )}{' '}
          회원 정보
        </ModalHeader>
        <ModalBody>
          <h5>{seatData.phoneNum}</h5>
          <Btns>
            {seatData.type !== 4 && seatData.type !== 'seminar' && (
              <ButtonS bg=" #FFC42B" color="#0F2A4A" onClick={showChangeSeatModal}>
                좌석이동 <ArrowRight />
              </ButtonS>
            )}

            <ButtonS bg="#21D7D9" color="#0F2A4A" onClick={toggleIsSendMsg}>
              문자발송 <MessageSquare />
            </ButtonS>
            <ButtonS bg="#722DE2 " onClick={showDetailModal}>
              상세정보 <Info />
            </ButtonS>
            <ButtonS bg="#F53C14" onClick={outTicket}>
              퇴장 <LogOut />
            </ButtonS>
          </Btns>
          <HistorySection>
            <HistoryToggle type="button" onClick={toggleHistory}>
              <span>최근 7일 이용 이력</span>
              {historyOpen ? <ChevronUp /> : <ChevronDown />}
            </HistoryToggle>
            {historyOpen &&
              (historyLoading ? (
                <HistoryEmpty>불러오는 중...</HistoryEmpty>
              ) : historyRows.length === 0 ? (
                <HistoryEmpty>최근 7일 이용 이력이 없습니다.</HistoryEmpty>
              ) : (
                <HistoryTable>
                  <thead>
                    <tr>
                      <th>일시</th>
                      <th>상태</th>
                      <th>전화번호</th>
                      <th>이용권</th>
                      <th>권번</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows.map((row, idx) => (
                      <tr key={`${row.ticketId}-${row.dateTime}-${idx}`}>
                        <td>{row.dateTime}</td>
                        <td>{row.message}</td>
                        <td
                          style={{
                            color: '#0f2a4a',
                            cursor: row.phoneNum && row.phoneNum !== '-' ? 'pointer' : 'default',
                            textDecoration:
                              row.phoneNum && row.phoneNum !== '-' ? 'underline' : 'none',
                          }}
                          onClick={() => handleHistoryPhoneClick(row.phoneNum)}>
                          {row.phoneNum}
                        </td>
                        <td>{row.ticketLabel}</td>
                        <td>{row.ticketId ?? '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </HistoryTable>
              ))}
          </HistorySection>
        </ModalBody>
      </Modal>
      <ModalBackground />
    </>
  );
};

export default ChoiceModal;
